import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { useDelegatingRequests } from '@/entities/delegating-request';
import { NotificationTypeToText } from '@/entities/notification';
import { generateTemplate } from '@/features/delegate/helpers';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { CryptoCore } from '@/shared/lib/secure-json/core/crypto-core';
import { generateMnemonic } from '@/shared/lib/secure-json/core/crypto-core/mnemonic';
import { CollectionName, useSJDatabase } from '@/shared/lib/sj-orm';
import { AssistantMnemonicDto } from '@/shared/lib/sj-orm/models/assistant-mnemonic.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { Textarea } from '@/shared/uikitV2/Textarea';
import { copy } from '@/shared/utils/misc';
import { GenerateRegistrationLink } from '@/widgets/generate-registration-link';

import { BtnClose } from '../../components/BtnClose';
import { useModalsStore } from '../../modals.store';
import { DelegateToModalProps } from '../../types';

import { Body, Container, Header, Info, StyledDrawer } from './styled';

type DelegatingRequestDataType = {
  name: string;
  mnemonic: string;
};

const SYSTEM_DEFAULT_ASSISTANT_NAME = 'SYSTEM_DEFAULT_ASSISTANT_NAME';

function hasStringProp<T extends string>(entity: unknown, prop: T): entity is { [K in T]: string } {
  return (
    typeof entity === 'object' &&
    entity !== null &&
    prop in entity &&
    typeof (entity as Record<string, unknown>)[prop] === 'string'
  );
}

export const DelegateToModal: FC<DelegateToModalProps> = ({ delegationId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'delegateTo' });
  const modals = useModalsStore();
  const { keyPair } = useKeyPairStore();
  const sjDb = useSJDatabase();

  const { getDelegatingRequest, grantAssistantAccess } = useDelegatingRequests();
  const notices = useNotices();
  const api = useApi();

  const [delegatingData, setDelegatingData] = useState<DelegatingRequestDataType | null>(null);
  const [link, setLink] = useState('');

  const relatedEntityData = useMemo(() => {
    const relatedEntity = getDelegatingRequest(delegationId)?.relatedEntity;
    if (!relatedEntity) return null;

    return sjDb.collections[relatedEntity.collectionName]?.findById(relatedEntity.dtoId);
  }, [delegationId, getDelegatingRequest]);

  const getAssistantPublicKey = async (mnemonic: string) => {
    const cryptoCore = new CryptoCore();
    const assistantKeyPair = await cryptoCore.mnemonicToKeyPair(mnemonic);
    return assistantKeyPair.publicKey;
  };

  const delegateToAssistant = async (assistant: AssistantMnemonicDto, documentId?: string) => {
    const assistantPublicKey = await getAssistantPublicKey(assistant.mnemonic);
    await grantAssistantAccess(delegationId, assistantPublicKey);

    setDelegatingData({
      name: assistant.name,
      mnemonic: assistant.mnemonic,
    });

    if (documentId && relatedEntityData) {
      const entityName =
        (hasStringProp(relatedEntityData, 'name') && relatedEntityData.name) ||
        (hasStringProp(relatedEntityData, 'nickname') && relatedEntityData.nickname) ||
        (hasStringProp(relatedEntityData, 'nickName') && relatedEntityData.nickName) ||
        '';

      notices.success(`${NotificationTypeToText.SENT_REQUEST_TO_ASSISTANT} ${entityName}`);
    }
  };

  // TODO: refactoring move to custom hook
  const getDefaultAssistant = async (): Promise<AssistantMnemonicDto | null> => {
    const { assistantMnemonic } = sjDb.collections;
    if (!assistantMnemonic) return null;

    const existAssistant = assistantMnemonic.findOne(
      (item) => item.name === SYSTEM_DEFAULT_ASSISTANT_NAME,
    );

    if (existAssistant) {
      return existAssistant;
    }

    const cryptoCore = new CryptoCore();
    const mnemonic = await generateMnemonic();
    const assistantKeyPair = await cryptoCore.mnemonicToKeyPair(mnemonic);

    const creationResult = await api.createAssistant({
      name: SYSTEM_DEFAULT_ASSISTANT_NAME,
      publicKey: assistantKeyPair.publicKey,
    });

    if (!creationResult.data) {
      return null;
    }

    const assistant = await assistantMnemonic.create(
      {
        id: sjDb.generateId(CollectionName.ASSISTANT_MNEMONIC),
        assistantProfileId: String(creationResult.data.id),
        name: SYSTEM_DEFAULT_ASSISTANT_NAME,
        mnemonic,
      },
      [keyPair!.publicKey, assistantKeyPair.publicKey],
    );

    if (!assistant?.assistantProfileId) {
      return null;
    }

    return assistant;
  };

  const handleChoseAssistant = async () => {
    const assistant = await getDefaultAssistant();
    if (assistant) {
      await delegateToAssistant(assistant, relatedEntityData?.id);
    }
  };

  const handleClose = useCallback(() => modals.closeModal(), [modals]);
  const handleAction = useCallback(async () => {
    if (!delegatingData?.mnemonic) return;
    try {
      await copy(generateTemplate(link, delegatingData.mnemonic));
      notices.success('Link and seed phrase were copied');
      modals.closeModal();
    } catch (e) {
      console.error(e);
    }
  }, [link, delegatingData]);

  return (
    <>
      <StyledDrawer width={400} footer={null} closable={false} onClose={handleClose} open>
        <Container>
          <Header>
            <Title size={22} bold>
              {t('title')}
            </Title>
            <BtnClose style={{ position: 'absolute', right: 24, top: 24 }} onClick={handleClose} />
          </Header>

          <Body className="uikitScrollbar">
            <Info>
              <Title size={18} bold>
                {t('subTitle')}
              </Title>
              <div className="description">{t('description')}</div>
            </Info>
            {delegatingData && (
              <div className="text-areas">
                <GenerateRegistrationLink
                  alias="assistant"
                  itemId={delegationId}
                  onLinkGenerated={(value) => setLink(value)}
                  textareaProps={{
                    label: t('linkLabel'),
                    withCopy: true,
                    readOnly: true,
                    height: 100,
                  }}
                />
                <Textarea
                  label={t('mnemonicLabel')}
                  value={delegatingData.mnemonic}
                  withCopy
                  readOnly
                  separator={' '}
                  height={100}
                />
              </div>
            )}
            <div>
              <Button
                style={{
                  marginBottom: 8,
                  background: COLORS.systemSky200,
                  width: '100%',
                  fontSize: 14,
                }}
                onClick={delegatingData ? handleAction : handleChoseAssistant}
              >
                {delegatingData ? t('copyAction') : t('generateLink')}
              </Button>
              {delegatingData && <div className="post-scriptum">{t('postScriptum')}</div>}
            </div>
          </Body>
        </Container>
      </StyledDrawer>
    </>
  );
};
